/*
 * "Ready to file": the profile measured against what an open case's forms
 * need. A notice case (RFE, NOID, NOIR) is measured differently: "Response
 * readiness" is the Notice Brief's issues against the response draft's
 * scorecard. Pure, so the card and its tests share it.
 */
import dayjs, { type Dayjs } from "dayjs";

import type {
  ForeignNational,
  StatusHealth
} from "@/api/types/foreignNational";
import type { ForeignNationalPassport } from "@/api/types/foreignNationalRecords";
import { answersIssue } from "@/api/types/draft";
import type { NoticeBriefPayload } from "@/features/keyFacts/types";

import { formatShortDate } from "../../header/clientHeaderModel";
import type { Coverage } from "../../lib/coverage";
import { isPermanentStatus } from "./identityModel";

/** USCIS and consulates expect a passport valid six months past filing. */
const PASSPORT_MARGIN_MONTHS = 6;

export type ReadinessTarget =
  | "personal"
  | "passports"
  | "immigration"
  | "ask_address"
  | "ask_employment"
  | "case";

export type ReadinessCheck = {
  id: string;
  ok: boolean;
  text: string;
  action?: { label: string; target: ReadinessTarget };
};

type Input = {
  client: ForeignNational | null;
  health: StatusHealth | null | undefined;
  passports: ForeignNationalPassport[];
  address: Coverage | null;
  employment: Coverage | null;
  /** The case's filing deadline, or today. */
  filingBy: Dayjs;
  firstName: string | null;
};

const fmt = (d: Dayjs) => formatShortDate(d.format("YYYY-MM-DD"));
const day = (iso: string) => dayjs(iso.slice(0, 10));

const currentPassport = (
  passports: ForeignNationalPassport[],
  fallback?: string | null
): Dayjs | null => {
  const current =
    passports.find((p) => p.is_current) ??
    [...passports].sort((a, b) =>
      String(b.expiration_date ?? "").localeCompare(
        String(a.expiration_date ?? "")
      )
    )[0];
  const raw = current?.expiration_date ?? fallback;
  const d = raw ? day(raw) : null;
  return d?.isValid() ? d : null;
};

const statusCheck = (
  client: ForeignNational | null,
  health: StatusHealth | null | undefined
): ReadinessCheck => {
  const status = (health?.status ?? client?.current_immigration_status ?? "")
    .toString()
    .trim();
  const fix = { label: "Review", target: "immigration" as const };
  if (!status)
    return {
      id: "status",
      ok: false,
      text: "Current status isn't on file",
      action: fix
    };
  if (isPermanentStatus(status))
    return { id: "status", ok: true, text: `${status} is on file` };
  if (health?.state === "expired" && health.ends)
    return {
      id: "status",
      ok: false,
      text: `${status} status ended ${fmt(day(health.ends))}`,
      action: { label: "Add Record", target: "immigration" }
    };
  if (health?.state === "unknown")
    return {
      id: "status",
      ok: false,
      text: `${status} end date isn't on file`,
      action: fix
    };
  if (health?.duration_of_status && !health.ends)
    return { id: "status", ok: true, text: `${status} for duration of status` };
  return {
    id: "status",
    ok: true,
    text: health?.ends
      ? `${status} is current through ${fmt(day(health.ends))}`
      : `${status} is current`
  };
};

const passportCheck = (
  client: ForeignNational | null,
  passports: ForeignNationalPassport[],
  filingBy: Dayjs
): ReadinessCheck => {
  const expires = currentPassport(passports, client?.passport_expiration);
  const action = { label: "Update", target: "passports" as const };
  if (!expires)
    return {
      id: "passport",
      ok: false,
      text: "No passport expiration on file",
      action
    };
  if (expires.isBefore(dayjs(), "day"))
    return {
      id: "passport",
      ok: false,
      text: `Passport expired ${fmt(expires)}`,
      action
    };
  if (expires.isBefore(filingBy.add(PASSPORT_MARGIN_MONTHS, "month"), "day"))
    return {
      id: "passport",
      ok: false,
      text: `Passport expires ${fmt(expires)}, under 6 months after filing`,
      action
    };
  return { id: "passport", ok: true, text: "Passport is valid through filing" };
};

const coverageCheck = (
  id: "address" | "employment",
  coverage: Coverage | null,
  firstName: string | null
): ReadinessCheck | null => {
  if (!coverage) return null;
  const noun = id === "address" ? "Addresses" : "Employment";
  const span = coverage.target.span.toLowerCase();
  if (coverage.gaps.length === 0)
    return { id, ok: true, text: `${noun} cover the last ${span}` };
  return {
    id,
    ok: false,
    text: `${noun} cover ${coverage.coveredLabel} of ${span}`,
    action: {
      label: firstName ? `Ask ${firstName}` : "Ask the Client",
      target: id === "address" ? "ask_address" : "ask_employment"
    }
  };
};

const birthCheck = (client: ForeignNational | null): ReadinessCheck => {
  const missing = [
    !client?.birthday && "date",
    !client?.city_of_birth && "city",
    !client?.country_of_birth && "country"
  ].filter(Boolean) as string[];
  if (missing.length === 0)
    return { id: "birth", ok: true, text: "Birth date and place are on file" };
  return {
    id: "birth",
    ok: false,
    text: `Birth ${missing.join(", ")} missing`,
    action: { label: "Fill In", target: "personal" }
  };
};

const citizenshipCheck = (client: ForeignNational | null): ReadinessCheck => {
  const has =
    (client?.citizenships?.length ?? 0) > 0 ||
    Boolean(client?.country_of_citizenship);
  return has
    ? { id: "citizenship", ok: true, text: "Citizenship is on file" }
    : {
        id: "citizenship",
        ok: false,
        text: "Citizenship is missing",
        action: { label: "Fill In", target: "personal" }
      };
};

/** Open items first, so the card leads with what to do. */
export const readinessChecks = (input: Input): ReadinessCheck[] => {
  const checks = [
    statusCheck(input.client, input.health),
    passportCheck(input.client, input.passports, input.filingBy),
    coverageCheck("address", input.address, input.firstName),
    coverageCheck("employment", input.employment, input.firstName),
    birthCheck(input.client),
    citizenshipCheck(input.client)
  ].filter((c): c is ReadinessCheck => c !== null);
  return [...checks.filter((c) => !c.ok), ...checks.filter((c) => c.ok)];
};

/** A notice workflow: the case answers an RFE, NOID or NOIR. */
export const isNoticeWorkflow = (flags: {
  isRfe: boolean;
  workflowType: string | null;
}): boolean =>
  flags.workflowType === "rfe" ||
  flags.workflowType === "noir_noid" ||
  (flags.isRfe && !flags.workflowType);

const plural = (n: number, word: string) => `${n} ${word}${n === 1 ? "" : "s"}`;

/**
 * "Response readiness": one check per issue the notice raises, read against
 * the response draft's scorecard and the brief's evidence gaps. Open items
 * first.
 */
export const responseChecks = (
  payload: NoticeBriefPayload | null
): ReadinessCheck[] => {
  const open = { label: "Open Case", target: "case" as const };
  const brief = payload?.brief ?? null;
  if (!brief)
    return [
      {
        id: "brief",
        ok: false,
        text: "The notice hasn't been read yet",
        action: open
      }
    ];
  const label = brief.label || "notice";
  const response = payload?.response ?? null;
  const scores = new Map(
    (response?.scorecard?.issues ?? []).map((i) => [i.key, i])
  );
  const checks: ReadinessCheck[] = [];
  if (!response?.has_content)
    checks.push({
      id: "draft",
      ok: false,
      text: `No ${label} response draft yet`,
      action: open
    });
  for (const issue of [...brief.issues].sort((a, b) => a.order - b.order)) {
    const score = scores.get(issue.key);
    const gaps = issue.evidence.reduce((n, row) => n + row.gaps.length, 0);
    const id = `issue:${issue.key}`;
    if (!score || !answersIssue(score)) {
      checks.push({
        id,
        ok: false,
        text: score
          ? `${issue.title} needs work in the letter`
          : `${issue.title} isn't answered yet`,
        action: open
      });
    } else if (gaps > 0 || score.status === "needs_evidence") {
      checks.push({
        id,
        ok: false,
        text: `${issue.title}: ${plural(Math.max(gaps, 1), "evidence gap")}`,
        action: open
      });
    } else {
      checks.push({ id, ok: true, text: `${issue.title} is answered` });
    }
  }
  return [...checks.filter((c) => !c.ok), ...checks.filter((c) => c.ok)];
};
